import React, { createContext, useContext, useRef } from "react";
import axios from "axios";
import { EResourceFormat } from "../dto/EResourceFormat";

interface EResource {
  hostBibliographicId: string;
  availabilitySources: AvailabilitySource[];
}

// equivalent to EIntegrationManager.getProductName values
export enum AvailabilitySourceService {
  overdrive = "overdrive",
  bakertaylor = "bakertaylor",
  mackin = "mackin",
}

export interface AvailabilitySource {
  service: AvailabilitySourceService; // string on backend
  availability: boolean; // For Mackin true if copiesAvailable is null or copiesAvailable > 0
  copiesOwned: number;
  copiesAvailable: number;
  numberOfHolds: number;
  alwaysAvailable: boolean; // For Mackin true if copiesAvailable is null
  preRelease: boolean;
  releaseDate: string;
  downloadLink: string;
  preferred: boolean;
  itemId: string;
  /**
   * Baker and Taylor always sets to true
   * Overdrive always sets to true
   * Mackin sets to true if you can check it out or place a hold
   */
  canCheckout: boolean;
  checkoutUrl: string;
  alreadyCheckedOut: boolean;
  /**
   * Dependent on LS2 Admin and Content Server (aka full eIntegration)
   */
  actionsEnabled: boolean;
  formatInfo: EResourceFormat[];
  invalidUser: boolean;
  hasItemsOverdue: boolean;
  hasUnpaidFines: boolean;
  hasExpiredCard: boolean;
}

interface EResourceAvailabilityRequest {
  hostBibliographicId: string;
  useLoggedInUserCredentials: boolean;
}

// Constants
const TTL = 5 * 60 * 1000;
const BATCH_WINDOW = 200;
const MAX_BATCH_SIZE = 25;

// Utility
function getDownloadableSource(
  availabilitySources: AvailabilitySource[]
): AvailabilitySource | null {
  return (
    availabilitySources.find((s) => s.availability && s.preferred) ||
    availabilitySources.find((s) => s.availability) ||
    availabilitySources.find((s) => s.preferred) ||
    availabilitySources[0] ||
    null
  );
}

// Internal types
type CacheEntry<T> = { data: T; timestamp: number };
type EResourceCache = Map<string, CacheEntry<EResource>>;
type InFlightMap = Map<string, Promise<AvailabilitySource | null>>;

type BatchRequest = {
  id: string;
  resolve: (data: AvailabilitySource | null) => void;
  reject: (err: unknown) => void;
};

// Context type
interface EResourceAvailabilityContextType {
  getAvailabilityForEResource: (
    hostBibliographicId: string
  ) => Promise<AvailabilitySource | null>;
}

// Context + Hook
const EResourceAvailabilityContext =
  createContext<EResourceAvailabilityContextType | null>(null);

export const useEResourceAvailability = () => {
  const ctx = useContext(EResourceAvailabilityContext);
  if (!ctx) {
    throw new Error(
      "useEResourceAvailability must be used inside its Provider"
    );
  }
  return ctx;
};

// Provider
export const EResourceAvailabilityProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const cacheRef = useRef<EResourceCache>(new Map());
  const inFlightRef = useRef<InFlightMap>(new Map());
  const batchQueueRef = useRef<BatchRequest[]>([]);
  const batchTimerRef = useRef<number | null>(null);

  const queueAvailability = (
    id: string
  ): Promise<AvailabilitySource | null> => {
    const now = Date.now();

    const cached = cacheRef.current.get(id);
    if (
      cached &&
      now - cached.timestamp < TTL &&
      cached.data.availabilitySources.length > 0
    ) {
      return Promise.resolve(
        getDownloadableSource(cached.data.availabilitySources)
      );
    }

    if (inFlightRef.current.has(id)) {
      return inFlightRef.current.get(id)!;
    }

    const promise = new Promise<AvailabilitySource | null>(
      (resolve, reject) => {
        batchQueueRef.current.push({ id, resolve, reject });
      }
    );

    inFlightRef.current.set(id, promise);

    if (!batchTimerRef.current) {
      batchTimerRef.current = window.setTimeout(flushBatch, BATCH_WINDOW);
    }

    return promise;
  };

  const flushBatch = async () => {
    const batch = [...batchQueueRef.current];
    batchQueueRef.current = [];
    batchTimerRef.current = null;

    const now = Date.now();

    // Split into sub-batches of <= 25
    const batches: BatchRequest[][] = [];
    for (let i = 0; i < batch.length; i += MAX_BATCH_SIZE) {
      batches.push(batch.slice(i, i + MAX_BATCH_SIZE));
    }

    await Promise.all(
      batches.map(async (subBatch) => {
        const payload: EResourceAvailabilityRequest[] = subBatch.map(
          ({ id }) => ({
            hostBibliographicId: id,
            useLoggedInUserCredentials: false,
          })
        );

        try {
          const { data } = await axios.post<EResource[]>(
            `/eResource/availability?_=${now}`,
            payload
          );

          const lookup = new Map<string, EResource>();
          data.forEach((res) => {
            lookup.set(res.hostBibliographicId, res);
          });

          subBatch.forEach(({ id, resolve }) => {
            const resource = lookup.get(id);
            if (
              resource &&
              resource.availabilitySources &&
              resource.availabilitySources.length > 0
            ) {
              cacheRef.current.set(id, { data: resource, timestamp: now });
              inFlightRef.current.delete(id);
              resolve(getDownloadableSource(resource.availabilitySources));
            } else {
              inFlightRef.current.delete(id);
              resolve(null);
            }
          });
        } catch (err) {
          subBatch.forEach(({ id, reject }) => {
            inFlightRef.current.delete(id);
            reject(err);
          });
        }
      })
    );
  };

  const getAvailabilityForEResource = async (
    hostBibliographicId: string
  ): Promise<AvailabilitySource | null> => {
    return queueAvailability(hostBibliographicId);
  };

  return (
    <EResourceAvailabilityContext.Provider
      value={{ getAvailabilityForEResource }}
    >
      {children}
    </EResourceAvailabilityContext.Provider>
  );
};
